import { NavigationSection } from '@app/context/constants';
import { parseNavState } from '@app/context/utils';
import { useUser } from '@app/context/UserContext';
import { useNavigation } from '@app/hooks/useNavigation';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { copyToClipboard } from '@app/utils/copyToClipboard';
import { Button, Input, Spinner } from '@components/ui';
import { useQuestionnaireDocumentShareQuery } from '@dieterApi/questionnaire/useQuestionnaireDocumentShareQuery';
import { Task } from '@dieterApi/task/useTaskQuery';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PeopleIcon from '@mui/icons-material/People';
import { Button as MuiButton, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  task: Task;
  type: 'Document' | 'Questionnaire';
  onConfirm: () => void;
}

export function PublishProcessBody({ task, type, onConfirm }: Props) {
  const { t } = useTranslation();
  const { user } = useUser();
  const { questionnaires } = useUserWithCompany();
  const { navigateDashboard } = useNavigation();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [getToken, { data, loading }] = useQuestionnaireDocumentShareQuery();
  const token = data?.getQuestionnaireShareToken;

  const url = token ? window.location.origin + '/share?token=' + token : '';

  // Only fetch the share token when there's no navStateOnComplete redirect
  useEffect(() => {
    if (task.navStateOnComplete) return;

    if (type == 'Document') {
      const taskQuestionnaire = questionnaires?.find((q) => q.application.id === task.applicationId);
      taskQuestionnaire &&
        getToken({
          variables: { localQuestionnaireId: taskQuestionnaire?.id, type, documentNumber: task.section },
        });
    } else {
      getToken({
        variables: { applicationId: task.applicationId, type, documentNumber: task.section },
      });
    }
  }, []);

  // When navStateOnComplete is set: navigate to that section instead of showing a copy-link
  if (task.navStateOnComplete) {
    const handleNavigate = () => {
      const newState = parseNavState(task.navStateOnComplete!);
      navigateDashboard(
        newState.activeSection || NavigationSection.Training,
        newState.activeTopic,
        newState.activeSubSection
      );
      window.scrollTo(0, 0);
      onConfirm();
    };

    return (
      <div className="flex flex-col gap-[10px] flex-1">
        <div>{task.confirmationText}</div>
        {!task.done && (
          <div className="flex gap-[10px]">
            <div className="flex-4" />
            <div className="flex-2">
              <Button onClick={handleNavigate} className="text-base" data-testid="button-navigate-training">
                <PeopleIcon fontSize="small" className="mr-1" />
                {t('dashboard.topic.process.publishprocess.go_to_training', 'Zum Schulungsbereich')}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const handleClick = () => {
    if (!showConfirmDialog) {
      setShowConfirmDialog(true);
    } else {
      // set Task to Done
      onConfirm();
    }
  };

  const handleAbortClick = () => {
    setShowConfirmDialog(false);
  };

  const handleCopyClick = () => {
    copyToClipboard(url).then(() => setHasCopied(true));
  };

  return token ? (
    <div className="flex flex-col gap-[10px] flex-1">
      {!showConfirmDialog ? (
        <>
          <div>{task.actionText}</div>
          <div className="flex">
            <div className="flex-1">
              <Input
                type="text"
                value={url}
                onFocus={(e) => e.target.select()}
                textSize={'small'}
                readOnly={true}
                data-testid={'input-share-url'}
              />
            </div>
            <div>
              <Tooltip
                enterTouchDelay={0}
                leaveTouchDelay={5000}
                title={
                  hasCopied
                    ? t('dashboard.topic.process.publishprocess.copied')
                    : t('dashboard.topic.process.publishprocess.clipboard')
                }
              >
                <MuiButton
                  onClick={handleCopyClick}
                  onMouseLeave={() => setTimeout(() => setHasCopied(false), 1000)}
                  data-testid={'button-copy-clipboard'}
                >
                  <ContentCopyIcon />
                </MuiButton>
              </Tooltip>
            </div>
          </div>
        </>
      ) : (
        <div>{task.confirmationText}</div>
      )}
      {!task.done && (
        <div className="flex gap-[10px]">
          <div className="flex-4" />
          <div className="flex-2">
            <Button onClick={handleClick} className="text-base">
              {showConfirmDialog ? t('common.yes') : t('common.finish')}
            </Button>
          </div>
          {showConfirmDialog && (
            <div className="flex-2">
              <Button onClick={handleAbortClick} btnColor="gray" className="text-base">
                {t('common.no')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  ) : (
    <Spinner />
  );
}
